import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { APPLICATION_TYPE_OPTIONS, PPE_OPTIONS } from 'src/app/shared/constants/spray-record-options';
import { ApiError } from 'src/app/abstract/api-error';
import { AdminDroneFlightEntry } from 'src/app/core/data-models/admin-drone-flight-entry.model';
import { AdminSprayRecord } from 'src/app/core/data-models/admin-spray-record.model';
import { FlightPathData } from 'src/app/core/data-models/flight-path-data.model';
import { SprayRecordService } from 'src/app/core/data-services/spray-record.service';
import { FlightMapComponent } from "src/app/shared/components/flight-map/flight-map.component";
import { formatCalendarDate, formatClockTime, toDisplayDate } from 'src/app/shared/utils/date.utils';

/**
 * Flight Record Detail Component
 * Read-only admin view of a single spray record with all related data.
 * Modeled after the user-side spray-record-details component.
 */
@Component({
  selector: 'app-flight-record-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, FlightMapComponent, PhoneFormatPipe],
  templateUrl: './flight-record-detail.component.html',
  styleUrls: ['./flight-record-detail.component.scss']
})
export class FlightRecordDetailComponent implements OnInit {
  recordId: string | null = null;
  record: AdminSprayRecord | null = null;
  loading = true;
  errorMessage = '';

  // Flight map modal
  showFlightMapModal = false;
  flightMapModalFlight: AdminDroneFlightEntry | null = null;
  showAllFlights = false;

  // Back-to-organization navigation
  fromOrgId: string | null = null;

  constructor(
    private sprayRecordService: SprayRecordService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.recordId = this.route.snapshot.paramMap.get('id');
    this.fromOrgId = this.route.snapshot.queryParamMap.get('fromOrg');

    if (this.recordId) {
      this.loadRecord(this.recordId);
    } else {
      this.errorMessage = 'No record ID provided';
      this.loading = false;
    }
  }

  async loadRecord(id: string): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.record = await firstValueFrom(this.sprayRecordService.getRecord(id));
      if (!this.record) {
        this.errorMessage = 'Spray record not found';
      }
    } catch (error) {
      console.error('Error loading flight record:', error);
      this.errorMessage = ApiError.fromHttpError(error).status === 404
        ? 'Spray record not found'
        : 'Failed to load flight record';
    } finally {
      this.loading = false;
    }
  }

  goBack(): void {
    if (this.fromOrgId) {
      this.router.navigate(['/organizations', this.fromOrgId], {
        queryParams: { tab: 'sprayRecords' }
      });
    } else {
      this.router.navigate(['/flight-records']);
    }
  }

  // Status helpers
  getStatusText(status: string | null | undefined): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status ?? '';
    }
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  // Chemical source badge
  getSourceBadgeClass(source: string): string {
    switch (source) {
      case 'book': return 'bg-primary-subtle text-primary';
      case 'custom': return 'bg-info-subtle text-info';
      case 'template': return 'bg-success-subtle text-success';
      default: return 'bg-secondary-subtle text-secondary';
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'book': return 'Book';
      case 'custom': return 'Custom';
      case 'template': return 'Template';
      default: return source;
    }
  }

  // Formatting helpers
  /**
   * `application_date` / `certification_expiration` are Postgres `date` columns
   * that the UTC Hub serializes as midnight-UTC. Formatting those through
   * `new Date(...)` rendered the previous day in every US timezone.
   */
  formatDate(date: string | null | undefined): string {
    return formatCalendarDate(
      date,
      { year: 'numeric', month: 'long', day: 'numeric' },
      'N/A'
    );
  }

  /**
   * Flight times arrive as full ISO timestamps ("2026-07-29T10:00:00.000Z"),
   * not bare "HH:MM" strings. Splitting on ':' parsed the *year* as the hour,
   * which is what turned a 10 AM – 2 PM flight into "10:00 PM – 10:00 PM".
   */
  formatTime(time: string | null | undefined): string {
    return formatClockTime(time);
  }

  // Incident helpers
  get hasAnyIncidents(): boolean {
    if (!this.record?.incidentInfo) return false;
    const info = this.record.incidentInfo;
    return info.takeOffOrLandingDamage === true || info.equipmentMalfunction === true || info.hasAccident === true;
  }

  // Totals
  get totalAcreageSprayed(): number {
    if (!this.record?.droneFlights) return 0;
    return this.record.droneFlights.reduce((sum, flight) => sum + flight.acreageSprayed, 0);
  }

  // Application type labels
  //
  // Both label maps are derived from the shared option lists rather than kept
  // by hand. The previous hardcoded maps listed values the spray-record form
  // never writes (aerial/ground/seed-treatment for types, kebab-case slugs for
  // PPE), so every real value fell through and rendered as its raw slug.
  getApplicationTypeLabels(): string[] {
    if (!this.record?.applicationTypeInfo?.applicationTypes) return [];
    const typeLabels = new Map<string, string>(
      APPLICATION_TYPE_OPTIONS.map(o => [o.value as string, o.label as string])
    );
    return this.record.applicationTypeInfo.applicationTypes.map(
      (type: string) => typeLabels.get(type) || type
    );
  }

  // PPE labels
  getPpeLabels(): string[] {
    if (!this.record?.safetyCompliance?.ppeRequired) return [];
    const ppeLabels = new Map<string, string>(
      PPE_OPTIONS.map(o => [o.value as string, o.label as string])
    );
    return this.record.safetyCompliance.ppeRequired.map(
      (ppe: string) => ppeLabels.get(ppe) || ppe
    );
  }

  // Certification helpers
  get certificationName(): string {
    return this.record?.certificationName || 'N/A';
  }

  get certificationNumber(): string {
    return this.record?.certificationNumber || 'N/A';
  }

  get certificationState(): string {
    return this.record?.certificationState || 'N/A';
  }

  get certificationExpiration(): string {
    if (!this.record?.certificationExpiration) return 'N/A';
    return this.formatDate(this.record.certificationExpiration);
  }

  isCertificationExpired(): boolean {
    if (!this.record?.certificationExpiration) return false;
    const expDate = toDisplayDate(this.record.certificationExpiration);
    if (!expDate || isNaN(expDate.getTime())) return false;
    return expDate.getTime() <= Date.now();
  }

  isCertificationExpiringSoon(): boolean {
    if (!this.record?.certificationExpiration) return false;
    const expDate = toDisplayDate(this.record.certificationExpiration);
    if (!expDate || isNaN(expDate.getTime())) return false;
    const now = Date.now();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return expDate.getTime() > now && expDate.getTime() - now <= thirtyDays;
  }

  // Flight map modal
  viewFlightPath(flight: AdminDroneFlightEntry): void {
    this.flightMapModalFlight = flight;
    this.showAllFlights = false;
    this.showFlightMapModal = true;
  }

  viewAllFlightPaths(): void {
    this.flightMapModalFlight = null;
    this.showAllFlights = true;
    this.showFlightMapModal = true;
  }

  closeFlightMapModal(): void {
    this.showFlightMapModal = false;
    this.flightMapModalFlight = null;
    this.showAllFlights = false;
  }

  getAllFlightPaths(): FlightPathData[] {
    if (!this.record) return [];
    return this.record.droneFlights
      .filter(f => f.flightPath !== null)
      .map(f => f.flightPath!);
  }

  getFieldCenter(): { lat: number; lng: number } | null {
    const lat = this.record?.fieldLatitude;
    const lng = this.record?.fieldLongitude;
    if (lat && lng) {
      return { lat, lng };
    }
    return null;
  }
}
